import { Link, useParams } from 'react-router-dom';

import { PageMeta } from '@/components/PageMeta';
import { ROUTES } from '@/config/routes';
import { getCompetitionApi, getCompetitionResultsApi } from '@/lib/api/competitionsApi';
import { COMPETITION_TYPE_LABELS as TYPE_LABELS, formatDateFullFr } from '@shared';
import { CompetitionStatus } from '@shared/enums/domain.enum';
import type { CompetitionResult } from '@shared/types/entities';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, Calendar, Clock, MapPin, Medal, Trophy, Users } from 'lucide-react';

function PlayersList({ players }: { players: CompetitionResult['team']['players'] }) {
  if (!players.length) return null;

  return (
    <p className='text-sm text-slate-500 mt-1'>
      {players.map(p => (
        <span key={p.userId} className={'block'}>
          <Link to={`/profil/${p.userId}`}>{`${p.firstName} ${p.lastName}`}</Link>
        </span>
      ))}
    </p>
  );
}

export default function ConcoursResultats() {
  const { id } = useParams<{ id: string }>();
  const compId = Number(id);

  const {
    data: competition,
    isLoading: loadingComp,
    isError: competitionError,
  } = useQuery({
    queryKey: ['competition', compId],
    queryFn: () => getCompetitionApi(compId),
    enabled: !!compId,
  });

  const { data: results = [], isLoading: loadingResults } = useQuery({
    queryKey: ['competition-results', compId],
    queryFn: () => getCompetitionResultsApi(compId),
    enabled: !!compId && !!competition && !competitionError,
  });

  const isLoading = loadingComp || loadingResults;
  const isFinished = competition?.status === CompetitionStatus.FINISHED;
  const top3 = results.slice(0, 3);

  if (isLoading) {
    return (
      <div className='min-h-screen bg-[#F8FAFC] animate-pulse'>
        <div className='h-80 bg-slate-200' />
        <div className='max-w-5xl mx-auto px-6 py-12 space-y-6'>
          <div className='h-8 bg-slate-200 rounded-xl w-1/3 mx-auto' />
          <div className='h-64 bg-slate-200 rounded-3xl' />
          <div className='h-96 bg-slate-200 rounded-3xl' />
        </div>
      </div>
    );
  }

  if (competitionError || !competition) {
    return (
      <div className='min-h-screen bg-[#F8FAFC] flex items-center justify-center'>
        <div className='max-w-lg mx-auto px-6'>
          <div className='bg-white border border-slate-200 rounded-3xl shadow-sm p-8 text-center'>
            <div className='w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4'>
              <Trophy className='w-8 h-8 text-amber-600' />
            </div>
            <h1 className='text-2xl font-bold text-slate-900 mb-2'>Résultats introuvables</h1>
            <p className='text-slate-500 mb-6'>
              Ce concours n&apos;existe pas ou n&apos;est pas accessible publiquement.
            </p>
            <Link
              to={ROUTES.SEARCH_CONCOURS}
              className='inline-flex items-center justify-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-xl font-bold hover:bg-blue-700 transition-colors'
            >
              Voir les concours disponibles
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='bg-[#F8FAFC] text-slate-900 font-sans'>
      <PageMeta
        title={`Résultats — ${competition.name}`}
        description={`Classement final du concours de pétanque ${competition.name} — ${competition.currentTeams} équipes participantes.`}
      />
      {/* Hero Banner */}
      <section className='h-80 bg-linear-to-br from-blue-600 via-purple-600 to-purple-800 relative flex items-center justify-center overflow-hidden'>
        <div className='text-center text-white relative z-10 px-6 mt-10'>
          <div className='inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6'>
            <Trophy className='w-4 h-4 text-yellow-300' />
            <span className='text-xs font-bold uppercase tracking-wider'>
              {isFinished ? 'Concours terminé' : 'Résultats'}
            </span>
          </div>

          <h1 className='text-4xl md:text-5xl font-extrabold mb-6'>{competition.name}</h1>

          <div className='flex flex-wrap items-center justify-center gap-6 text-blue-200'>
            <span className='flex items-center gap-1.5 uppercase'>
              <Calendar className='w-4 h-4' /> {competition.date ? formatDateFullFr(competition.date) : '—'}
            </span>
            {(competition.city ?? competition.venue) && (
              <span className='flex items-center gap-1.5'>
                <MapPin className='w-4 h-4' /> {competition.city ?? competition.venue}
              </span>
            )}
            <span className='flex items-center gap-1.5'>
              <Users className='w-4 h-4' />{' '}
              {competition.type ? (TYPE_LABELS[competition.type] ?? competition.type) : '—'}
            </span>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className='px-6 -mt-8 relative z-20'>
        <div className='max-w-5xl mx-auto'>
          <div className='grid grid-cols-2 md:grid-cols-4 gap-4'>
            {[
              { label: 'Équipes', value: competition.currentTeams.toString() },
              {
                label: 'Joueurs',
                value: competition.playersPerTeam
                  ? (competition.currentTeams * competition.playersPerTeam).toString()
                  : '—',
              },
              {
                label: 'Format',
                value: competition.type ? (TYPE_LABELS[competition.type] ?? competition.type) : '—',
              },
              {
                label: 'Classés',
                value: results.length > 0 ? `${results.length}` : '—',
              },
            ].map(stat => (
              <div key={stat.label} className='bg-white rounded-2xl border border-slate-200 shadow-lg p-4 text-center'>
                <div className='text-xl font-extrabold text-slate-900'>{stat.value}</div>
                <div className='text-xs text-slate-500 mt-0.5'>{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Results section */}
      {!isFinished ? (
        <section className='px-6 py-20'>
          <div className='max-w-2xl mx-auto text-center'>
            <Clock className='w-16 h-16 text-slate-300 mx-auto mb-4' />
            <h2 className='text-2xl font-extrabold text-slate-700 mb-2'>Résultats pas encore disponibles</h2>
            <p className='text-slate-500'>Les résultats seront publiés à la fin du concours.</p>
          </div>
        </section>
      ) : results.length === 0 ? (
        <section className='px-6 py-20'>
          <div className='max-w-2xl mx-auto text-center'>
            <Trophy className='w-16 h-16 text-slate-300 mx-auto mb-4' />
            <h2 className='text-2xl font-extrabold text-slate-700 mb-2'>Aucun résultat saisi</h2>
            <p className='text-slate-500'>L&apos;organisateur n&apos;a pas encore publié les résultats.</p>
          </div>
        </section>
      ) : (
        <>
          {/* Podium */}
          <section className='px-6 py-20'>
            <div className='max-w-4xl mx-auto'>
              <h2 className='text-3xl font-extrabold text-center text-slate-900 mb-12'>Podium</h2>

              <div className='flex items-end justify-center gap-4 md:gap-8'>
                {/* 2nd */}
                {top3[1] && (
                  <div className='flex-1 max-w-60'>
                    <div className='bg-linear-to-br from-slate-200 to-slate-300 rounded-3xl p-6 text-center'>
                      <div className='text-4xl mb-2'>🥈</div>
                      <div className='w-14 h-14 bg-white rounded-full flex items-center justify-center mx-auto mb-3 shadow-md'>
                        <span className='text-2xl font-extrabold text-slate-500'>2</span>
                      </div>
                      <div className='bg-white rounded-2xl p-4 shadow-sm'>
                        <h3 className='font-extrabold text-slate-900 text-lg'>{top3[1].team.name}</h3>

                        <PlayersList players={top3[1].team.players} />
                        {top3[1].points !== null && (
                          <div className='mt-2 text-sm font-semibold text-slate-500'>{top3[1].points} pts</div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* 1st */}
                {top3[0] && (
                  <div className='flex-1 max-w-60 scale-110 z-10'>
                    <div className='bg-linear-to-br from-yellow-300 via-yellow-400 to-amber-500 rounded-3xl p-6 text-center shadow-xl'>
                      <div className='text-5xl mb-2'>🥇</div>
                      <div className='w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-3 shadow-lg'>
                        <span className='text-3xl font-extrabold text-yellow-600'>1</span>
                      </div>
                      <div className='bg-white rounded-2xl p-4 shadow-sm'>
                        <h3 className='font-extrabold text-slate-900 text-lg'>{top3[0].team.name}</h3>
                        <PlayersList players={top3[0].team.players} />
                        {top3[0].points !== null && (
                          <div className='mt-2 text-sm font-semibold text-yellow-600'>{top3[0].points} pts</div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* 3rd */}
                {top3[2] && (
                  <div className='flex-1 max-w-60'>
                    <div className='bg-linear-to-br from-amber-200 to-amber-400 rounded-3xl p-6 text-center'>
                      <div className='text-4xl mb-2'>🥉</div>
                      <div className='w-14 h-14 bg-white rounded-full flex items-center justify-center mx-auto mb-3 shadow-md'>
                        <span className='text-2xl font-extrabold text-amber-700'>3</span>
                      </div>
                      <div className='bg-white rounded-2xl p-4 shadow-sm'>
                        <h3 className='font-extrabold text-slate-900 text-lg'>{top3[2].team.name}</h3>
                        <PlayersList players={top3[2].team.players} />
                        {top3[2].points !== null && (
                          <div className='mt-2 text-sm font-semibold text-amber-700'>{top3[2].points} pts</div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* Full Results Table */}
          <section className='px-6 pb-16'>
            <div className='max-w-5xl mx-auto'>
              <div className='bg-white rounded-3xl border border-slate-200 overflow-hidden'>
                <div className='flex items-center justify-between p-6 border-b border-slate-200'>
                  <h2 className='text-2xl font-extrabold text-slate-900'>Classement complet</h2>
                  <span className='text-sm text-slate-500'>{results.length} équipes</span>
                </div>

                <div className='overflow-x-auto'>
                  <table className='w-full'>
                    <thead>
                      <tr className='bg-slate-50'>
                        <th className='px-6 py-4 text-left text-xs font-bold text-slate-500 uppercase tracking-wider'>
                          Rang
                        </th>
                        <th className='px-6 py-4 text-left text-xs font-bold text-slate-500 uppercase tracking-wider'>
                          Équipe
                        </th>
                        <th className='px-6 py-4 text-left text-xs font-bold text-slate-500 uppercase tracking-wider hidden md:table-cell'>
                          Joueurs
                        </th>
                        {/*<th className='px-6 py-4 text-center text-xs font-bold text-slate-500 uppercase tracking-wider'>*/}
                        {/*  Points*/}
                        {/*</th>*/}
                      </tr>
                    </thead>
                    <tbody>
                      {results.map(r => (
                        <tr
                          key={r.id}
                          className={`border-b border-slate-100 ${
                            r.rank === 1
                              ? 'bg-yellow-50'
                              : r.rank === 2
                                ? 'bg-slate-50'
                                : r.rank === 3
                                  ? 'bg-amber-50'
                                  : 'bg-white'
                          }`}
                        >
                          <td className='px-6 py-4'>
                            <div className='flex items-center gap-2'>
                              {r.rank <= 3 ? (
                                <Medal
                                  className={`w-5 h-5 ${
                                    r.rank === 1
                                      ? 'text-yellow-500'
                                      : r.rank === 2
                                        ? 'text-slate-400'
                                        : 'text-amber-700'
                                  }`}
                                />
                              ) : (
                                <span className='w-5 text-center font-bold text-slate-400 text-sm'>{r.rank}</span>
                              )}
                              <span className='font-extrabold text-slate-900'>{r.rank}</span>
                            </div>
                          </td>
                          <td className='px-6 py-4 font-bold text-slate-900'>{r.team.name}</td>
                          <td className='px-6 py-4 text-sm text-slate-500 hidden md:table-cell'>
                            {r.team.players.map((p, i) => (
                              <span key={p.id}>
                                {i > 0 && ', '}
                                {p.userId !== null ? (
                                  <Link to={`/profil/${p.userId}`} className='hover:text-blue-600 hover:underline'>
                                    {`${p.firstName} ${p.lastName}`}
                                  </Link>
                                ) : (
                                  `${p.firstName} ${p.lastName}`
                                )}
                              </span>
                            ))}
                          </td>
                          {/*<td className='px-6 py-4 text-center font-bold text-slate-900'>*/}
                          {/*  {r.points !== null ? r.points : '—'}*/}
                          {/*</td>*/}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </section>
        </>
      )}

      {/* CTA */}
      <section className='px-6 py-20'>
        <div className='max-w-4xl mx-auto'>
          <div className='bg-linear-to-br from-blue-600 via-purple-600 to-purple-700 rounded-3xl p-12 text-center text-white relative overflow-hidden'>
            <div className='absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2' />
            <div className='absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2' />

            <div className='relative z-10'>
              <Trophy className='w-12 h-12 text-yellow-300 mx-auto mb-4' />
              <h2 className='text-3xl md:text-4xl font-extrabold mb-4'>Participer à un concours</h2>
              <p className='text-blue-100 text-lg mb-8 max-w-xl mx-auto'>
                Trouvez les prochains concours près de chez vous et inscrivez votre équipe.
              </p>
              <Link
                to={ROUTES.SEARCH_CONCOURS}
                className='bg-white text-blue-700 px-8 py-4 rounded-2xl font-bold text-lg hover:bg-blue-50 transition-colors inline-flex items-center gap-2'
              >
                Voir les prochains concours <ArrowRight className='w-5 h-5' />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
